import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuFileSpreadsheet, LuLoaderCircle, LuPlus, LuUserCheck, LuUserX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import PageHero from '../../../components/data/PageHero';
import StatTiles from '../../../components/data/StatTiles';
import { useTranslation } from '../../../i18n/LanguageContext';

/** Title card of Müşteri Yönetimi with the page-level actions. */
export const CustomerPageHeader = ({ isExporting, onDownload, onAdd }) => {
  const { t } = useTranslation();
  return (
    <PageHero
      icon={LuBuilding2}
      gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30"
      title={t('customers.title')}
      subtitle={t('customers.subtitle')}
      // Phones: "add" across the row and an icon-only export beside it
      actionsClassName="tw-grid tw-grid-cols-[minmax(0,1fr)_auto] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap"
    >
      <Button
        onClick={onDownload}
        disabled={isExporting}
        aria-label={t('customers.actions.download')}
        title={t('customers.actions.downloadHint')}
        className="tw-order-2 max-sm:tw-w-10 max-sm:tw-px-0 sm:tw-order-1"
      >
        {isExporting
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin tw-text-emerald-600 dark:tw-text-emerald-400" />
          : <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />}
        <span className="tw-hidden sm:tw-inline">{t('customers.actions.download')}</span>
      </Button>
      <Button variant="primary" onClick={onAdd} className="tw-order-1 max-sm:tw-w-full sm:tw-order-2">
        <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('customers.actions.add')}
      </Button>
    </PageHero>
  );
};

CustomerPageHeader.propTypes = {
  isExporting: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

/** Total / active / inactive customer counts. */
export const CustomerStats = ({ total, active, inactive, ready }) => {
  const { t } = useTranslation();
  const shareOf = (count) => (ready && total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => t('customers.stats.share', { percent: Math.round(shareOf(count) * 100) });

  return (
    <StatTiles
      ready={ready}
      tiles={[
        {
          key: 'total',
          label: t('customers.stats.total'),
          shortLabel: t('customers.stats.totalShort'),
          value: total,
          icon: LuBuilding2,
          gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30',
        },
        {
          key: 'active',
          label: t('customers.stats.active'),
          shortLabel: t('customers.stats.activeShort'),
          value: active,
          share: shareOf(active),
          shareTitle: shareTitle(active),
          icon: LuUserCheck,
          gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
          barClass: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500',
        },
        {
          key: 'inactive',
          label: t('customers.stats.inactive'),
          shortLabel: t('customers.stats.inactiveShort'),
          value: inactive,
          share: shareOf(inactive),
          shareTitle: shareTitle(inactive),
          icon: LuUserX,
          gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
          barClass: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500',
        },
      ]}
    />
  );
};

CustomerStats.propTypes = {
  total: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired,
  inactive: PropTypes.number.isRequired,
  ready: PropTypes.bool,
};
